import {
  DATA_LAYER_EVENTS,
  getScreenFromLocation,
  convertDataAttributeToObjects,
  addScreenEventIfItChanged,
  dmpgLayerNotAlreadyInitializedForThisScreen,
  Smdl,
  SmdlAssets,
  SmdlEvent,
  SmdlModule,
  SmdlScreen,
  DataLayerEvent,
  SmdlUser,
} from '@smarty-nx/tracking'

import omit from 'lodash/omit'

export const buildUser = (): SmdlUser => {
  const state = JSON.parse(localStorage.getItem('state') as string)
  if (state && state.auth && state.auth.jwt && state.actingAs !== null) {
    return {
      status: 'logged-in' as const,
      customer: {
        id: state.customerId,
        plan: state.customerPlan,
        actingAs: state.actingAs,
      },
    }
  } else if (state && state.auth && state.auth.jwt) {
    return {
      status: 'logged-in' as const,
      customer: {
        id: state.customerId,
        plan: state.customerPlan,
      },
    } as SmdlUser
  }
  return {
    status: 'logged-out' as const,
  } as SmdlUser
}

const elements = (parent: Document | HTMLElement, selector: string) =>
  Array.from(parent.querySelectorAll<HTMLElement>(selector))

const element = (parent: Document | HTMLElement, selector: string) =>
  parent.querySelector<HTMLElement>(selector)

const buildItem = (el: HTMLElement) => ({
  id: el.dataset?.itemId,
  type: el.getAttribute('data-type'),
  position: el.getAttribute('data-position'),
})

const findItems = (module: HTMLElement, slug?: string) => {
  return elements(
    module,
    slug ? `[data-item-id="${slug}"]` : '[data-item-id]'
  ).map(buildItem)
}

export const findModules = (
  moduleId: string,
  parentEl: Document | HTMLElement = document,
  slug?: string
): SmdlModule[] =>
  elements(parentEl, `[data-module-id="${moduleId}"]`).map((el) => ({
    ...convertDataAttributeToObjects(el).module,
    id: el.getAttribute('data-module-id'),
    items: findItems(el, slug),
  }))

const buildItemWithAssetData = (el: HTMLElement) => {
  const { asset, type, discount } = convertDataAttributeToObjects(el)
  return {
    ...asset,
    ...buildDiscount(discount),
    type,
    id: el.getAttribute('data-item-id'),
  }
}

export const findAssets = (
  parentEl: Document | HTMLElement = document,
  slug?: string
): SmdlAssets =>
  elements(parentEl, slug ? `[data-item-id="${slug}"]` : '[data-item-id]')
    .map(buildItemWithAssetData)
    .reduce((acc, item) => {
      if (!acc[item.type as string]) {
        acc[item.type as string] = []
      }
      acc[item.type as string].push(omit(item, ['type']))

      return acc
    }, {}) as SmdlAssets

export const findProducts = (parentEl: Document | HTMLElement = document) =>
  elements(parentEl, '[data-product_id]')
    .map(convertDataAttributeToObjects)
    .map(({ product }) => product)

const buildDiscount = (discount) => {
  if (discount) {
    return { discount: { lines: [discount] } }
  }
  return undefined
}

const buildCart = (parentEl: Document | HTMLElement) => {
  const cartEl = element(parentEl, '[data-cart_checkout-step]')
  if (cartEl) {
    const { cart } = convertDataAttributeToObjects(cartEl)

    const productLines = elements(parentEl, '[data-lines_id]')
      .map(convertDataAttributeToObjects)
      .map(({ lines, discount }) => ({
        product: lines,
        ...buildDiscount(discount),
      }))

    return { ...cart, lines: productLines }
  }

  return undefined
}

const buildTransaction = (parentEl: Document | HTMLElement) => {
  const transactionEl = element(parentEl, '[data-transaction_id]')
  if (transactionEl) {
    const { transaction } = convertDataAttributeToObjects(transactionEl)
    return transaction
  }

  return undefined
}

export const moduleEvent = (
  container: Document | HTMLElement,
  slug: string,
  moduleId: string
): DataLayerEvent => ({
  event: DATA_LAYER_EVENTS.MODULE_LOAD,
  modules: findModules(moduleId, container, slug),
  assets: findAssets(container, slug),
  user: buildUser(),
})

const buildEventsBasedOnScreenType = (
  screen: SmdlScreen | undefined,
  container: Document | HTMLElement
) => {
  const { type = '', name = '' } = screen || {}
  let events: DataLayerEvent[] = []

  switch (type) {
    case 'product-details':
      return findProducts(container).map((product) => ({
        product,
        event: DATA_LAYER_EVENTS.PRODUCT_VIEW,
        user: buildUser(),
      }))
    case 'checkout':
      const cart = buildCart(container)
      if (name === 'plans-confirmation') {
        const transaction = buildTransaction(container)
        if (transaction && transaction.id) {
          events = [
            {
              event: DATA_LAYER_EVENTS.TRANSACTION_SUCCESS,
              transaction: { ...transaction, cart },
              user: buildUser(),
            },
          ]
        } else {
          events = []
        }
      } else if (name === 'plans-2') {
        events = [
          {
            event: DATA_LAYER_EVENTS.REGISTRATION_SUCCESS,
            user: buildUser(),
          },
        ]
        if (cart) {
          events.push({
            event: DATA_LAYER_EVENTS.CART_LOAD,
            cart: buildCart(container),
            user: buildUser(),
          })
        }
      } else {
        events = []
        if (cart) {
          events.push({
            event: DATA_LAYER_EVENTS.CART_LOAD,
            cart: buildCart(container),
            user: buildUser(),
          })
        }
      }
      return events
    default:
      return events
  }
}

const attachClickEvent = (
  container,
  ele,
  smdlObject,
  clickEvent = 'data-dmpg-event-name'
) =>
  (ele.onclick = () =>
    smdlObject.events.push({
      event: ele.getAttribute(clickEvent),
      ...convertDataAttributeToObjects(ele).event,
    }))

const attachAllClickEvents = (
  container: Document | HTMLElement,
  smdlObject: Smdl
) => {
  elements(container, '[data-dmpg-event-name]').forEach((el) =>
    attachClickEvent(container, el, smdlObject)
  )
  elements(container, '[data-item-id]').forEach((el) =>
    attachClickEvent(container, el, smdlObject, 'data-item-id')
  )
}

const completeInitializationForScreen = (
  completedEvents,
  smdlObject,
  container
) => {
  completedEvents.forEach((completedEvent) =>
    smdlObject.events.push(completedEvent)
  )
  attachAllClickEvents(container, smdlObject)
}

const buildDmpgDataAttributeListener = (
  container: Document | HTMLElement,
  smdlObject: Smdl,
  getScreen: () => SmdlScreen | undefined
) => {
  let previouslyAttachedScreen: string | null = null
  return () => {
    smdlObject.screen = getScreen() || smdlObject.screen
    smdlObject.user = buildUser()
    if (smdlObject.screen) {
      addScreenEventIfItChanged(smdlObject)
    }
    if (
      dmpgLayerNotAlreadyInitializedForThisScreen(
        smdlObject.screen,
        previouslyAttachedScreen
      )
    ) {
      const completedEvents = buildEventsBasedOnScreenType(
        smdlObject?.screen,
        container
      )

      if (completedEvents.length > 0) {
        completeInitializationForScreen(completedEvents, smdlObject, container)
        previouslyAttachedScreen = smdlObject?.screen?.name || null
      }
    }
  }
}

export const attach = (
  container: Document | HTMLElement,
  smdlObject: Smdl,
  getScreen: () => SmdlScreen | undefined = getScreenFromLocation
) => {
  const dmpgListener = buildDmpgDataAttributeListener(
    container,
    smdlObject,
    getScreen
  )
  const observer = new MutationObserver(dmpgListener)
  observer.observe(container, { childList: true, subtree: true })
  return observer
}

const buildForm = (el) => {
  const form = el.querySelector('form')
  if (form) {
    return {
      id: form.id,
      name: form.getAttribute('data-form-name'),
    }
  }

  return undefined
}

const eventExtras = (eventName: string, container: HTMLElement | Document) => {
  const form = buildForm(container)
  if (form) return { form }
  if (eventName === 'user.interact.auto.success.loginSuccess') {
    return { user: buildUser() }
  }
  return {}
}

export const addTrackEvent = (
  event: DataLayerEvent,
  params: { extras: boolean } = { extras: false }
) => {
  const extras = params.extras ? eventExtras(event.event, document) : {}
  const e = { ...event, ...extras, user: buildUser() }

  window.smDl.events.push(e)
}
